/**
 * Flash-E - Technical Interview Flashcards
 * Copyright (c) 2025 Steve Crittenden
 * Licensed under AGPL-3.0
 *
 * https://github.com/critter/flash-e
 */

import React, { useEffect, useState } from 'react';

interface BuildInfo {
  commitHash: string;
  branch: string;
  buildTime: string;
  version: string;
}

const Footer: React.FC = () => {
  const [buildInfo, setBuildInfo] = useState<BuildInfo | null>(null);

  useEffect(() => {
    // Try to load build info from public/build-info.json
    fetch('/build-info.json')
      .then(res => res.json())
      .then(data => setBuildInfo(data))
      .catch(() => {
        // Fallback: build info not available (development mode)
        setBuildInfo(null);
      });
  }, []);

  const commitHash = buildInfo?.commitHash || 'dev';
  const githubCommitUrl = buildInfo?.commitHash && buildInfo.commitHash !== 'unknown'
    ? `https://github.com/scrittenden/flash-e/commit/${buildInfo.commitHash}`
    : 'https://github.com/scrittenden/flash-e';

  return (
    <footer className="bg-gray-100 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 mt-auto">
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          {/* Attribution */}
          <div className="text-sm text-gray-600 dark:text-gray-400">
            <p>
              Flash-E © 2025 Steve Crittenden |{' '}
              <a
                href="https://github.com/scrittenden/flash-e"
                className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                target="_blank"
                rel="noopener noreferrer"
              >
                Open Source (AGPL-3.0)
              </a>
              {' '}|{' '}
              <a
                href={githubCommitUrl}
                className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors font-mono text-xs"
                target="_blank"
                rel="noopener noreferrer"
                title={buildInfo ? `Built: ${new Date(buildInfo.buildTime).toLocaleString()}` : 'Development build'}
              >
                {commitHash}
              </a>
            </p>
          </div>

          {/* Donation and Links */}
          <div className="flex gap-4">
            <a
              href="https://github.com/scrittenden/flash-e"
              className="text-sm text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
              target="_blank"
              rel="noopener noreferrer"
            >
              ⭐ Star on GitHub
            </a>
            <a
              href="https://github.com/sponsors/scrittenden"
              className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-500 transition-colors"
              target="_blank"
              rel="noopener noreferrer"
            >
              💝 Support Development
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
